import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Input, InputGroup, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Users, Plus, Search, Edit, Trash2, Calendar } from 'react-feather';
import CountUp from 'react-countup';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

// ── Gradientes ciclados pelos cards de lista ──────────────────────────────────
const CARD_GRADIENTS = [
  { gradient: 'linear-gradient(26deg,#7366ff,#a26cf8)', color: '#7366ff', colorClass: 'primary'   },
  { gradient: 'linear-gradient(26deg,#f48665,#fda23f)', color: '#f48665', colorClass: 'warning'   },
  { gradient: 'linear-gradient(26deg,#04b1e0,#6ddaf1)', color: '#04b1e0', colorClass: 'info'      },
  { gradient: 'linear-gradient(26deg,#f9e473,#f3bd4e)', color: '#f3bd4e', colorClass: 'warning'   },
  { gradient: 'linear-gradient(26deg,#f73164,#ff8fa0)', color: '#f73164', colorClass: 'secondary' },
  { gradient: 'linear-gradient(26deg,#29d9d5,#1aacdc)', color: '#29d9d5', colorClass: 'info'      },
  { gradient: 'linear-gradient(26deg,#54ba4a,#16C7F9)', color: '#54ba4a', colorClass: 'success'   },
];

export default function Listas() {
  const navigate = useNavigate();
  const { user } = useProspectAuth();
  const isAdmin  = user?.role === 'admin';
  const [lists, setLists]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [deleteModal, setDeleteModal] = useState(null);

  async function fetchLists() {
    try {
      const { data } = await api.get('/lists');
      setLists(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  const searchRef = useRef();

  useEffect(() => { fetchLists(); }, []);
  useEffect(() => { searchRef.current?.focus(); }, []);

  async function handleDelete() {
    try {
      await api.delete(`/lists/${deleteModal.id}`);
      setDeleteModal(null);
      fetchLists();
    } catch (e) { alertErro('Erro ao excluir', e.response?.data?.error || ''); }
  }

  const filtered      = lists.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));
  const totalContacts = lists.reduce((s, l) => s + (l.total_contacts || 0), 0);

  return (
    <>
      <Breadcrumbs mainTitle='Listas de Contatos' parent='Topo Prospect' title='Listas' />
      <Container fluid>

        {isAdmin && (
          <div className='alert alert-secondary py-2 px-3 mb-3 d-flex align-items-center gap-2' style={{ fontSize: '0.8rem', color: '#495057' }}>
            <span>🔍</span> <span>Modo auditoria — faça login como tenant para operar</span>
          </div>
        )}

        {/* Header */}
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <div />
          {!isAdmin && (
            <Button color='primary' size='sm' onClick={() => navigate('/listas/nova')}>
              <Plus size={14} className='me-1' /> Adicionar lista
            </Button>
          )}
        </div>

        {/* ── Widget cards Cuba widget-1 ──────────────────────────────────── */}
        <Row className='mb-2'>
          <Col xl='3' md='6' sm='6' className='mb-4'>
            <Card className='widget-1'>
              <CardBody>
                <div className='widget-content'>
                  <div className='widget-round primary'>
                    <div className='bg-round' style={{ background: 'linear-gradient(26deg,#7366ff,#a26cf8)', overflow: 'hidden', width: 54, height: 54 }}>
                      <Users color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
                    </div>
                  </div>
                  <div>
                    <h4 className='mb-1 f-w-600'>
                      <CountUp end={lists.length} duration={1.2} />
                    </h4>
                    <span className='f-light f-12'>Total de listas</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl='3' md='6' sm='6' className='mb-4'>
            <Card className='widget-1'>
              <CardBody>
                <div className='widget-content'>
                  <div className='widget-round success'>
                    <div className='bg-round' style={{ background: 'linear-gradient(26deg,#54ba4a,#29d9d5)', overflow: 'hidden', width: 54, height: 54 }}>
                      <Users color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
                    </div>
                  </div>
                  <div>
                    <h4 className='mb-1 f-w-600'>
                      <CountUp end={totalContacts} duration={1.5} separator='.' />
                    </h4>
                    <span className='f-light f-12'>Total de contatos</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Busca */}
        <InputGroup className='mb-4' style={{ maxWidth: 340 }}>
          <span className='input-group-text bg-transparent'><Search size={14} /></span>
          <Input innerRef={searchRef} placeholder='Buscar lista...' value={search} onChange={e => setSearch(e.target.value)} bsSize='sm' />
        </InputGroup>

        {/* ── Grid de cards ──────────────────────────────────────────────── */}
        {loading ? (
          <div className='text-center py-5 text-muted'>Carregando...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardBody className='text-center py-5'>
              <Users size={48} className='mb-3 text-muted' />
              <h6 className='text-muted'>{search ? 'Nenhuma lista encontrada' : 'Nenhuma lista cadastrada ainda'}</h6>
              {!search && !isAdmin && (
                <>
                  <Button color='primary' size='sm' className='mt-2' onClick={() => navigate('/listas/nova')}>
                    Criar a primeira lista
                  </Button>
                  <p className='text-muted f-12 mt-2'>Importe contatos via CSV, grupos do WhatsApp ou adicione manualmente.</p>
                </>
              )}
            </CardBody>
          </Card>
        ) : (
          <Row>
            {filtered.map((list, idx) => {
              const theme = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];
              const cap   = list.capacity || 0;
              const fill  = cap > 0 ? Math.min(100, Math.round(((list.total_contacts || 0) / cap) * 100)) : 0;
              return (
                <Col xl='4' md='6' key={list.id} className='mb-4'>
                  <Card className='h-100' style={{ cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.transition = '0.5s'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
                  >
                    <CardBody style={{ padding: 24 }}>

                      {/* Topo: ícone circular gradiente + ações */}
                      <div className='d-flex align-items-start justify-content-between mb-3'>
                        <div
                          className='rounded-circle d-flex align-items-center justify-content-center'
                          style={{
                            width: 52, height: 52,
                            background: theme.gradient,
                            boxShadow: `0 4px 15px ${theme.color}55`,
                            flexShrink: 0,
                          }}
                        >
                          <Users size={22} color='#fff' strokeWidth={1.5} />
                        </div>
                        <div className='d-flex gap-1'>
                          <Button color='light' size='sm' className='py-1 px-2' title='Ver'
                            onClick={() => navigate(`/listas/${list.id}`)}>
                            <Edit size={13} />
                          </Button>
                          {!isAdmin && (
                            <Button color='light' size='sm' className='py-1 px-2' title='Excluir'
                              onClick={() => setDeleteModal(list)}>
                              <Trash2 size={13} className='text-danger' />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Nome da lista */}
                      <h6
                        className='mb-1 f-w-600'
                        style={{ color: theme.color, cursor: 'pointer', fontSize: 15 }}
                        onClick={() => navigate(`/listas/${list.id}`)}
                      >
                        {list.name}
                      </h6>

                      {/* Contadores */}
                      <div className='d-flex align-items-center gap-1 mb-3'>
                        <Users size={13} className='text-muted' />
                        <span className='f-12 text-muted'>
                          <strong style={{ color: theme.color }}>{(list.total_contacts || 0).toLocaleString('pt-BR')}</strong> contatos
                        </span>
                      </div>

                      {/* Barra de preenchimento (só se capacity > 0) */}
                      {cap > 0 && (
                        <div className='mb-3'>
                          <div className='d-flex justify-content-between mb-1'>
                            <span className='f-10 text-muted text-uppercase'>Preenchimento</span>
                            <span className='f-10 f-w-600' style={{ color: theme.color }}>{fill}%</span>
                          </div>
                          <div className='progress' style={{ height: 5, borderRadius: 10, background: '#f1f1f1' }}>
                            <div className='progress-bar' style={{ width: `${fill}%`, background: theme.gradient, borderRadius: 10 }} />
                          </div>
                        </div>
                      )}

                      {/* Rodapé: data */}
                      <div className='d-flex align-items-center gap-1 mt-auto pt-2 border-top'>
                        <Calendar size={11} className='text-muted' />
                        <small className='text-muted'>
                          {new Date(list.created_at).toLocaleDateString('pt-BR')}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        <div className='mt-1'>
          <small className='text-muted'>{filtered.length} lista{filtered.length !== 1 ? 's' : ''}</small>
        </div>

        {/* Modal excluir */}
        <Modal isOpen={!!deleteModal} toggle={() => setDeleteModal(null)}>
          <ModalHeader toggle={() => setDeleteModal(null)}>Excluir lista?</ModalHeader>
          <ModalBody>
            A lista <strong>{deleteModal?.name}</strong> e todos os seus{' '}
            <strong>{deleteModal?.total_contacts || 0}</strong> contatos serão removidos permanentemente.
          </ModalBody>
          <ModalFooter>
            <Button color='light' onClick={() => setDeleteModal(null)}>Cancelar</Button>
            <Button color='danger' onClick={handleDelete}>Excluir</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
}
